import { useCallback, useEffect, useMemo, useState } from 'react';
import { ITest } from '../../../../data/types';
import {
  DEFAULT_SPLIT_VIEW_CONFIG,
  ISplitColumnConfig,
  ISplitViewConfig,
  SplitColumnFilterType,
} from '../types';

interface UseSplitViewOptions {
  /** Initial split view configuration */
  initialConfig?: ISplitViewConfig;
}

interface UseSplitViewResult {
  /** Current split view configuration */
  config: ISplitViewConfig;
  /** Whether split view is enabled */
  isEnabled: boolean;
  /** Toggle split view on/off */
  toggleSplitView: () => void;
  /** Enable split view */
  enableSplitView: () => void;
  /** Disable split view */
  disableSplitView: () => void;
  /** Update left column configuration */
  updateLeftColumn: (updates: Partial<ISplitColumnConfig>) => void;
  /** Update right column configuration */
  updateRightColumn: (updates: Partial<ISplitColumnConfig>) => void;
  /** Set the full configuration */
  setConfig: (config: ISplitViewConfig) => void;
  /** Reset to default configuration */
  resetConfig: () => void;
  /** Filter tests for the left column */
  filterLeftColumn: (tests: ITest[]) => ITest[];
  /** Filter tests for the right column */
  filterRightColumn: (tests: ITest[]) => ITest[];
}

/**
 * Hook to manage split view state for the scoreboard.
 * Handles configuration and filtering of tests for each column.
 *
 * @param options - Configuration options
 * @returns Split view state and controls
 */
export const useSplitView = (
  options: UseSplitViewOptions = {}
): UseSplitViewResult => {
  const { initialConfig = DEFAULT_SPLIT_VIEW_CONFIG } = options;

  const [config, setConfigState] = useState<ISplitViewConfig>(initialConfig);

  // Memoize initialConfig based on meaningful fields to avoid JSON.stringify in deps
  const stableInitialConfig = useMemo(
    () => initialConfig,
    [
      initialConfig?.enabled,
      initialConfig?.leftColumn?.filterType,
      initialConfig?.leftColumn?.title,
      initialConfig?.leftColumn?.filteredIds?.join(','),
      initialConfig?.leftColumn?.tags?.map((t) => t.value).join(','),
      initialConfig?.leftColumn?.secondaryFilterIds?.join(','),
      initialConfig?.rightColumn?.filterType,
      initialConfig?.rightColumn?.title,
      initialConfig?.rightColumn?.filteredIds?.join(','),
      initialConfig?.rightColumn?.tags?.map((t) => t.value).join(','),
      initialConfig?.rightColumn?.secondaryFilterIds?.join(','),
    ]
  );

  useEffect((): void => {
    if (stableInitialConfig) {
      setConfigState(stableInitialConfig);
    }
  }, [stableInitialConfig]);

  const isEnabled = config.enabled;

  const toggleSplitView = useCallback((): void => {
    setConfigState((prev) => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  }, []);

  const enableSplitView = useCallback((): void => {
    setConfigState((prev) => ({
      ...prev,
      enabled: true,
    }));
  }, []);

  const disableSplitView = useCallback((): void => {
    setConfigState((prev) => ({
      ...prev,
      enabled: false,
    }));
  }, []);

  const updateLeftColumn = useCallback(
    (updates: Partial<ISplitColumnConfig>): void => {
      setConfigState((prev) => ({
        ...prev,
        leftColumn: {
          ...prev.leftColumn,
          ...updates,
        },
      }));
    },
    []
  );

  const updateRightColumn = useCallback(
    (updates: Partial<ISplitColumnConfig>): void => {
      setConfigState((prev) => ({
        ...prev,
        rightColumn: {
          ...prev.rightColumn,
          ...updates,
        },
      }));
    },
    []
  );

  const setConfig = useCallback((newConfig: ISplitViewConfig): void => {
    setConfigState(newConfig);
  }, []);

  const resetConfig = useCallback((): void => {
    setConfigState(DEFAULT_SPLIT_VIEW_CONFIG);
  }, []);

  // Create filter functions
  const createColumnFilter = useCallback(
    (columnConfig: ISplitColumnConfig) =>
      (tests: ITest[]): ITest[] => {
        const { filterType, filteredIds, secondaryFilterIds } = columnConfig;

        // If no filters are set, return all tests
        if (filteredIds.length === 0) {
          return tests;
        }

        return tests.filter((test) => {
          let matchesPrimary = true;
          if (filterType === 'team') {
            matchesPrimary = test.teams?.some((teamId) => filteredIds.includes(teamId)) ?? false;
          } else if (filterType === 'group') {
            matchesPrimary = test.groups?.some((groupId) => filteredIds.includes(groupId)) ?? false;
          }

          if (!matchesPrimary) return false;

          // Apply secondary filter if set
          if (secondaryFilterIds?.length > 0) {
            if (filterType === 'team') {
              // Primary is team, secondary is group
              return test.groups?.some((groupId) => secondaryFilterIds.includes(groupId)) ?? false;
            } else if (filterType === 'group') {
              // Primary is group, secondary is team
              return test.teams?.some((teamId) => secondaryFilterIds.includes(teamId)) ?? false;
            }
          }

          return true;
        });
      },
    []
  );

  const filterLeftColumn = useMemo(
    () => createColumnFilter(config.leftColumn),
    [createColumnFilter, config.leftColumn]
  );

  const filterRightColumn = useMemo(
    () => createColumnFilter(config.rightColumn),
    [createColumnFilter, config.rightColumn]
  );

  return {
    config,
    isEnabled,
    toggleSplitView,
    enableSplitView,
    disableSplitView,
    updateLeftColumn,
    updateRightColumn,
    setConfig,
    resetConfig,
    filterLeftColumn,
    filterRightColumn,
  };
};

/**
 * Helper to create a column config
 */
export const createColumnConfig = (
  title: string,
  filterType: SplitColumnFilterType,
  filteredIds: string[]
): ISplitColumnConfig => ({
  title,
  filterType,
  filteredIds,
});

/**
 * Helper to check if a column has any filters applied
 */
export const hasFiltersApplied = (columnConfig: ISplitColumnConfig): boolean =>
  columnConfig.filteredIds.length > 0;
